import { Component, EventEmitter, Input, Output } from '@angular/core';
import { getExtensionFromMimeType } from '../../utils/mime-utils';

/**
 * Thin orchestrator that selects the correct sub-component
 * (document-viewer, content-viewer, or form-editor) based on
 * the current input state, and manages shared panel chrome
 * (resize handle, header, width).
 */
@Component({
	selector: 'app-artifact-panel',
	templateUrl: './artifact-panel.component.html',
	styleUrls: ['./artifact-panel.component.scss'],
})
export class ArtifactPanelComponent {
	// ─── Inputs ────────────────────────────────────────────────────────────────
	/** Artifact content mode — renders markdown content instead of form fields */
	@Input() artifactTitle: string | null = null;
	@Input() artifactContent: string | null = null;

	/** Document viewer mode */
	@Input() documentBlob: Blob | null = null;
	@Input() documentContentType: string | null = null;

	@Output() closed = new EventEmitter<void>();

	// ─── State ─────────────────────────────────────────────────────────────────
	panelWidth = 420;
	private isResizing = false;
	private resizeStartX = 0;
	private resizeStartWidth = 0;

	// ─── Computed getters ──────────────────────────────────────────────────────

	/** Whether the panel is in document viewer mode */
	get isDocumentMode(): boolean {
		return !!this.documentBlob;
	}

	/** Whether the panel is in content/artifact mode */
	get isContentMode(): boolean {
		return !this.isDocumentMode && !!this.artifactContent;
	}

	/** Whether the document is a PDF (used for fullscreen button display) */
	get isDocumentPdf(): boolean {
		return !!this.documentContentType && this.documentContentType.includes('pdf');
	}

	/** Whether the document is a DOCX (used for fullscreen button display) */
	get isDocumentDocx(): boolean {
		return !!this.documentContentType && (this.documentContentType.includes('officedocument') || this.documentContentType.includes('msword'));
	}

	// ─── Public actions ────────────────────────────────────────────────────────

	/** Emits close event to the parent */
	onClose(): void {
		this.closed.emit();
	}

	/** Downloads the document blob with the correct file extension */
	onDownloadDocument(): void {
		if (!this.documentBlob) return;
		const url = URL.createObjectURL(this.documentBlob);
		const a = document.createElement('a');
		a.href = url;
		const ext = getExtensionFromMimeType(this.documentBlob.type);
		const base = this.artifactTitle || 'document';
		a.download = ext && !base.toLowerCase().endsWith(`.${ext}`) ? `${base}.${ext}` : base;
		a.click();
		URL.revokeObjectURL(url);
	}

	/** Begins a resize drag operation on the panel left edge */
	onResizeStart(event: MouseEvent): void {
		this.isResizing = true;
		this.resizeStartX = event.clientX;
		this.resizeStartWidth = this.panelWidth;
		event.preventDefault();

		const onMouseMove = (e: MouseEvent) => {
			if (!this.isResizing) return;
			const delta = this.resizeStartX - e.clientX;
			this.panelWidth = Math.min(Math.max(this.resizeStartWidth + delta, 300), window.innerWidth * 0.7);
		};

		const onMouseUp = () => {
			this.isResizing = false;
		};

		const overlay = document.createElement('div');
		overlay.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:9999;cursor:col-resize;';
		document.body.appendChild(overlay);

		overlay.addEventListener('mousemove', onMouseMove);
		overlay.addEventListener('mouseup', () => {
			onMouseUp();
			overlay.remove();
		});
		document.addEventListener('mouseup', () => {
			onMouseUp();
			overlay.remove();
		});
	}
}
